import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useRegisterSW } from "virtual:pwa-register/react";
import { Sentry } from "../lib/sentry";

const TOAST_ID = "app-update";
/** How often an open tab asks the server for a newer build. */
const CHECK_INTERVAL_MS = 60 * 60 * 1000;
/** A check on returning to the tab, at most this often. */
const RESUME_CHECK_MIN_MS = 60 * 1000;

/**
 * "A new version is ready" toast (vite-plugin-pwa `registerType: 'prompt'`).
 *
 * A new build never takes over a page on its own (src/sw.ts): it waits, and
 * this toast offers it. Reload activates it and the plugin reloads every
 * open Caliu tab onto it; dismissing it keeps the current build until the
 * next launch. Nothing reloads unless the user taps Reload.
 *
 * Mounted once at the app root.
 */
export function UpdatePrompt() {
  const { t } = useTranslation();
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      let lastCheck = Date.now();
      const check = () => {
        if (!navigator.onLine) return;
        lastCheck = Date.now();
        // Offline or the server unreachable: the next check retries. Not
        // reported, a failed update check is not an app error.
        registration.update().catch(() => {});
      };
      setInterval(check, CHECK_INTERVAL_MS);
      // iOS restores a suspended tab without reloading, so the browser never
      // looks for a new build by itself: look when the user comes back.
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState !== "visible") return;
        if (Date.now() - lastCheck >= RESUME_CHECK_MIN_MS) check();
      });
    },
    onRegisterError(err) {
      Sentry.captureException(err);
    },
  });

  useEffect(() => {
    if (!needRefresh) return;
    // Once offered, the new build only takes control because someone tapped
    // Reload (here or in another tab), and this page must then reload: it
    // runs the old build, whose chunks the new worker just removed. The
    // plugin does this itself only when workbox-window's `isUpdate` is true,
    // which is decided once at registration: false for a session that began
    // as a first visit, leaving that page stuck on the old build.
    const sw = "serviceWorker" in navigator ? navigator.serviceWorker : null;
    const reloadOnTakeover = () => window.location.reload();
    sw?.addEventListener("controllerchange", reloadOnTakeover, { once: true });
    toast(t("appUpdate.available"), {
      id: TOAST_ID,
      duration: Infinity,
      closeButton: true,
      action: {
        label: t("common.reload"),
        onClick: () => void updateServiceWorker(),
      },
    });
    return () => {
      sw?.removeEventListener("controllerchange", reloadOnTakeover);
      toast.dismiss(TOAST_ID);
    };
  }, [needRefresh, t, updateServiceWorker]);

  return null;
}
